import React from "react";
import { createRoot } from "react-dom/client";
import "./styles/globals.css";
import App from "./components/App/";
import { signIn } from "./utils/authn";
import apiProxy from "./api/apiProxy";
import { BehaviorSubject, combineLatest, forkJoin } from "rxjs";
import { map, mergeMap } from "rxjs/operators";
import useStreamedElement from "./utils/reactive/useStreamedElement";
import withLoadingAndErrorHandling from "./utils/reactive/wrapPageApiCall.js";
import ErrorView from "./components/ErrorView";
import { swallowApiErrors } from "./api/util";

// Suppress known third-party prop warnings from @sainsburys-tech/fable (unmaintained upstream bugs).
// TODO: Remove isDisabled/isSelected suppression once fable fixes these upstream.
const _consoleError = console.error;
console.error = (...args) => {
  if (typeof args[0] === 'string') {
    // React calls console.error with printf-style args: args[0] is the template, args[1] is the prop name
    if (args[0].includes('does not recognize') && (args[1] === 'isDisabled' || args[1] === 'isSelected')) return;
    if (args[0].includes('`isDisabled` prop') || args[0].includes('`isSelected` prop')) return;
    // Fable's TimePicker renders its time-options list without keys internally (fable bug, unfixable upstream)
    if (args[0].includes('Each child in a list') && args.some(a => typeof a === 'string' && a.includes('ForwardRef'))) return;
  }
  _consoleError.apply(console, args);
};

const renderError = () => {
  createRoot(document.getElementById("root")).render(<ErrorView />);
};

const renderApp = () => {
  createRoot(document.getElementById("root")).render(
    <StreamedApp
      getLightingStores={apiProxy.storeLighting.getStores}
      getEnabledFeatures={apiProxy.configuration.getEnabledFeatures}
      getEnabledLightingFeatures={
        apiProxy.lightingConfiguration.getEnabledLightingFeatures
      }
      getUserRoles={apiProxy.userRoles.getUserRoles}
      getEnergyStores={apiProxy.store.getStores}
    />
  );
};

function StreamedApp(props) {
  return useStreamedElement(props$ => {
    const fetchTrigger$ = new BehaviorSubject(null);

    const triggerFetch = () => {
      fetchTrigger$.next();
    };

    return withLoadingAndErrorHandling(() => {
      return combineLatest(props$, fetchTrigger$).pipe(
      mergeMap(([props]) => {
        return forkJoin(
          swallowApiErrors(props.getEnabledFeatures, { features: [] }),
          swallowApiErrors(props.getEnabledLightingFeatures, { features: [] }),
          swallowApiErrors(props.getUserRoles, { roles: [] }),
          swallowApiErrors(props.getLightingStores, { stores: [] }),
          swallowApiErrors(props.getEnergyStores, { stores: [] })
        );
      }),
      map(
        ([
          enabledFeatures,
          enabledLightingFeatures,
          userRoles,
          lightingStores,
          energyStores
        ]) => (
          <App
            enabledFeatures={enabledFeatures.features.concat(
              enabledLightingFeatures.features
            )}
            userRoles={userRoles.roles}
            stores={{
              energy: energyStores.stores,
              lighting: lightingStores.stores
            }}
            onLightingStoresChange={triggerFetch}
          />
        )
      )
    );
  });
  }, props);
}

signIn().then(
  ({ isSilentRefresh }) => {
    if (isSilentRefresh) {
      return;
    }
    renderApp();
  },
  (err) => {
    renderError();
  }
);
